import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, PenSquare, Eye, Pencil, Trash2, ArrowLeft, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  blogsAPI,
  resolveImageUrl,
  CATEGORY_LABELS,
} from '../services/blogsApi';

const StatusBadge = ({ status }) => {
  const map = {
    pending: { icon: Clock, cls: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Pending Review' },
    approved: { icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Live' },
    rejected: { icon: XCircle, cls: 'bg-red-100 text-red-800 border-red-200', label: 'Rejected' },
    draft: { icon: PenSquare, cls: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Draft' },
  };
  const conf = map[status] || map.draft;
  const Icon = conf.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${conf.cls}`}
      data-testid={`status-${status}`}
    >
      <Icon className="h-3 w-3" /> {conf.label}
    </span>
  );
};

const MyBlogsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState([]);
  const [author, setAuthor] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const authToken =
    localStorage.getItem('session_token') ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('hostToken');

  useEffect(() => {
    if (!authToken) {
      navigate('/login', { state: { from: '/blog/mine' }, replace: true });
      return;
    }
    fetch();
  }, []);

  const fetch = async () => {
    try {
      setLoading(true);
      const res = await blogsAPI.mine();
      setBlogs(res.data || []);
      setAuthor(res.author);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load your blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blog) => {
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) return;
    try {
      setDeletingId(blog.id);
      await blogsAPI.remove(blog.id);
      toast.success('Blog deleted');
      setBlogs((prev) => prev.filter((b) => b.id !== blog.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#7CB342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl py-8">
        <Button variant="ghost" onClick={() => navigate('/blog')} className="mb-4 text-gray-600">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Blog
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Blogs</h1>
            <p className="text-gray-500 text-sm mt-1">
              {author?.name} · {blogs.length} {blogs.length === 1 ? 'story' : 'stories'}
            </p>
          </div>
          <Button
            onClick={() => navigate('/blog/new')}
            className="bg-[#7CB342] hover:bg-[#6da036]"
            data-testid="new-blog-btn"
          >
            <PenSquare className="h-4 w-4 mr-2" /> Write a new blog
          </Button>
        </div>

        {blogs.length === 0 ? (
          <div className="text-center py-16 text-gray-500 bg-white rounded-lg border" data-testid="empty-my-blogs">
            <PenSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-gray-700">You haven&apos;t written any blogs yet</p>
            <p className="text-sm mt-1">Share a travel story or destination guide with the community.</p>
            <Button
              onClick={() => navigate('/blog/new')}
              className="mt-4 bg-[#7CB342] hover:bg-[#6da036]"
            >
              Write your first blog
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-blogs-list">
            {blogs.map((b) => (
              <Card key={b.id} data-testid={`my-blog-${b.id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {b.cover_image ? (
                      <img
                        src={resolveImageUrl(b.cover_image)}
                        alt=""
                        className="w-24 h-24 sm:w-32 sm:h-24 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=400';
                        }}
                      />
                    ) : (
                      <div className="w-24 h-24 sm:w-32 sm:h-24 bg-emerald-50 rounded-lg flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{b.title}</h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {CATEGORY_LABELS[b.category] || b.category}
                            {b.destination ? ` · ${b.destination}` : ''}
                          </p>
                        </div>
                        <StatusBadge status={b.status} />
                      </div>
                      {b.status === 'rejected' && b.rejection_reason && (
                        <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                          <strong>Admin note:</strong> {b.rejection_reason}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {b.status === 'approved' && (
                          <Link to={`/blog/${b.slug || b.id}`}>
                            <Button variant="outline" size="sm" data-testid={`view-blog-${b.id}`}>
                              <Eye className="h-3 w-3 mr-1" /> View
                            </Button>
                          </Link>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/blog/${b.id}/edit`)}
                          data-testid={`edit-blog-${b.id}`}
                        >
                          <Pencil className="h-3 w-3 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(b)}
                          disabled={deletingId === b.id}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`delete-blog-${b.id}`}
                        >
                          {deletingId === b.id ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <Trash2 className="h-3 w-3 mr-1" />
                          )}
                          Delete
                        </Button>
                        {b.status === 'approved' && (
                          <span className="text-xs text-gray-500 flex items-center gap-1 ml-auto">
                            <Eye className="h-3 w-3" /> {b.views || 0} views
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBlogsPage;
